/**
 * Fichas abertas neste cliente: uma réplica NDB por documento, sincronizada pelo
 * WebSocket (ndb.open / ndb.submit → ndb.snapshot / ndb.batches / ndb.reject).
 *
 * As escritas são otimistas (aparecem na hora) e confirmadas pela ordem do
 * servidor. Ao reconectar, cada documento é reaberto a partir do último lote
 * recebido e as escritas ainda não confirmadas são reenviadas.
 */
import type { ClientMessage, ServerMessage } from '../shared.js';
import { Replica, ReplicaNodeDatabase, testPermission, validateOps, type NodeSnapshot, type OpBatch, type Principal, type ReplicaAuthority, type SequencedBatch } from '../ndb.js';

type Sender = (message: ClientMessage) => void;
/** Quem usa o cliente na sala: login, grupos (todos + o do papel) e se é mestre (dono dos documentos). */
export type NdbIdentity = { who: Principal; owner: boolean } | null;

type Entry = {
  docId: string;
  replica: Replica | null;
  db: ReplicaNodeDatabase | null;
  refs: number;
  waiters: ((db: ReplicaNodeDatabase) => void)[];
  rejected: string[];
};

const clientId = `c${Math.random().toString(36).slice(2, 10)}`;
/**
 * Réplicas já abertas nesta página. Cada uma gera ids com um prefixo próprio: fechar e reabrir
 * uma ficha (trocar a aba do painel faz isso) recomeçaria a contagem, e os nós novos teriam os
 * ids dos que a réplica anterior criou ("node … already exists").
 */
let replicasOpened = 0;
const entries = new Map<string, Entry>();
let send: Sender = () => undefined;
let identity: () => NdbIdentity = () => null;

export function setNdbSender(fn: Sender): void {
  send = fn;
}

/**
 * Identidade usada para recusar já no cliente o que o servidor recusaria (mesmas
 * regras de permissão do NDB): escrever na ficha de outra pessoa não "pisca".
 */
export function setNdbIdentity(fn: () => NdbIdentity): void {
  identity = fn;
}

/** Quem escreve agora (login, grupos e se é mestre), ou null fora de uma sala. */
export function currentNdbIdentity(): NdbIdentity {
  return identity();
}

const rejectListeners = new Map<string, Set<(error: string) => void>>();

/** Avisa quando o servidor recusa uma escrita neste documento (plugins web repassam à página). */
export function onDocReject(docId: string, fn: (error: string) => void): () => void {
  let set = rejectListeners.get(docId);
  if (!set) rejectListeners.set(docId, (set = new Set()));
  set.add(fn);
  return () => {
    set!.delete(fn);
    if (set!.size === 0) rejectListeners.delete(docId);
  };
}

const authority: ReplicaAuthority = {
  check(tree, ops) {
    const id = identity();
    if (!id || id.owner) return null;
    return validateOps(tree, ops, (node, permission) => testPermission(tree, node, id.who, permission))?.error ?? null;
  },
  can(tree, node, permission) {
    const id = identity();
    return !id || id.owner || testPermission(tree, node, id.who, permission);
  },
};

/** Abre (ou reaproveita) a réplica de um documento; resolve quando o estado chegar. */
export function openDoc(docId: string): Promise<ReplicaNodeDatabase> {
  let entry = entries.get(docId);
  if (!entry) {
    entry = { docId, replica: null, db: null, refs: 0, waiters: [], rejected: [] };
    entries.set(docId, entry);
    send({ type: 'ndb.open', docId });
  }
  entry.refs += 1;
  if (entry.db) return Promise.resolve(entry.db);
  return new Promise((resolve) => entry!.waiters.push(resolve));
}

export function releaseDoc(docId: string): void {
  const entry = entries.get(docId);
  if (!entry) return;
  entry.refs -= 1;
  if (entry.refs > 0) return;
  entries.delete(docId);
  send({ type: 'ndb.close', docId });
}

/** Erros de escrita recusada (permissão), para mostrar na ficha. */
export function rejectedWrites(docId: string): string[] {
  return entries.get(docId)?.rejected ?? [];
}

/** Mensagens do servidor sobre documentos. Devolve true se tratou. */
export function handleNdbMessage(message: ServerMessage): boolean {
  switch (message.type) {
    case 'ndb.snapshot': {
      const entry = entries.get(message.docId);
      if (!entry) return true;
      const snapshot = message.snapshot as NodeSnapshot;
      if (entry.replica) {
        entry.replica.resync(snapshot, message.seq);
      } else {
        entry.replica = new Replica(`${clientId}-${(++replicasOpened).toString(36)}`, snapshot, message.seq);
        entry.db = new ReplicaNodeDatabase(message.docId, entry.replica, (batch: OpBatch) => send({ type: 'ndb.submit', docId: message.docId, batch }), authority);
        for (const w of entry.waiters.splice(0)) w(entry.db);
      }
      return true;
    }
    case 'ndb.batches': {
      const entry = entries.get(message.docId);
      if (!entry?.replica) return true;
      for (const batch of message.batches) entry.replica.receive(batch as SequencedBatch);
      return true;
    }
    case 'ndb.reject': {
      const entry = entries.get(message.docId);
      if (!entry?.replica) return true;
      entry.replica.reject(message.clientSeq);
      entry.rejected.push(message.error);
      for (const fn of rejectListeners.get(message.docId) ?? []) fn(message.error);
      return true;
    }
    case 'ndb.applied': {
      // reenviado ao reconectar, o lote já estava aplicado e no estado que chegou: sai da fila como uma
      // recusa sai (o visível é refeito sem ele), mas não é escrita recusada
      entries.get(message.docId)?.replica?.reject(message.clientSeq);
      return true;
    }
    default:
      return false;
  }
}

/** Conexão refeita (novo `ready`): reabre os documentos e reenvia o que não foi confirmado. */
export function resubscribeAll(): void {
  for (const entry of entries.values()) {
    if (!entry.replica) {
      send({ type: 'ndb.open', docId: entry.docId });
      continue;
    }
    send({ type: 'ndb.open', docId: entry.docId, sinceSeq: entry.replica.lastSeq });
    for (const batch of entry.replica.unconfirmed()) send({ type: 'ndb.submit', docId: entry.docId, batch });
  }
}

/** Saiu da sala: esquece tudo. */
export function resetDocs(): void {
  entries.clear();
}
